import React, { useState, useEffect, useCallback, useMemo } from "react";

import { Route, RouteComponentProps, Redirect, Switch } from "react-router";

import {
  fetchDashboardBillinBrief,
  fetchDashboardBillingCharts_V2,
  fetchInvoicePDF,
} from "libLegacy/api/dashboard";
import { PageStandard } from "../../../components/admin/PageStandard";
import {
  parseDateFromUrl,
  parseDateFromToday,
  parseDateFromPicker,
  formatChartFetchParams,
} from "logic/MyBills";
import { BillingProvider, BillingStore } from "contexts/BillingContext";
import { useToken, useUserContext } from "contexts/UserContext";
import { CredentialAlert } from "components/dashboard/CredentialAlert";

import { InvoiceDetails } from "./InvoiceDetails";
import { MonthlyBilling } from "./MonthlyBilling";
import { YearlyBilling } from "./YearlyBilling";
import { STATUS_CREDENTIAL } from "components/libComponents/CredentialStatusTexts";
import { AutomationSpace } from "api/automationTypes";

const tabLinkItems = [
  { label: "mensal", to: "/admin/billing/monthly" },
  { label: "anual", to: "/admin/billing/yearly" },
];

export const BillingMain = ({
  match: { path },
  location: { search, pathname },
  history,
}: RouteComponentProps) => {
  const [referenceDate, setReferenceDate] = useState<Date | null>(null);
  const [backendFilters, setBackendFilters] = useState<
    Partial<Record<"month" | "year", string>> | undefined
  >();
  const token = useToken();
  const [isLoading, setIsLoading] = useState<BillingStore["isLoading"]>(false);
  const [hasError, setHasError] = useState<BillingStore["hasError"]>(false);
  const [monthData, setMonthData] = useState<BillingStore["monthData"]>();
  const [yearData, setYearData] = useState<BillingStore["yearData"]>();
  const [hasAlert, setHasAlert] = useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const isYearly = useMemo(() => pathname.endsWith("/yearly"), [pathname]);
  const { currentSpace } = useUserContext();

  const fetchMonthData = useCallback(
    async (filterDate) => {
      try {
        const response = await fetchDashboardBillinBrief(
          token,
          filterDate,
          currentSpace?.contractCode || ""
        );
        setMonthData({ ...response, isProsumer: !!currentSpace?.isProsumer });

        if (response.statusEnergyOperator !== STATUS_CREDENTIAL.VALIDATED) {
          if (
            response.statusEnergyOperator === "PENDING" ||
            response.statusEnergyOperator === "MISSING"
          ) {
            history.replace("onboarding/no-invoices-credentials");
          } else {
            setHasAlert(true);
          }
        }
      } catch (error) {
        handleError(error, "Erro ao carregar os dados do mês");
      } finally {
        setIsLoading(false);
      }
    },
    [token, currentSpace, history]
  );

  const fetchYearData = useCallback(
    async (filterDate) => {
      const { month, year } = filterDate;
      const { from, to } = formatChartFetchParams(month, year);

      try {
        const charts = await fetchDashboardBillingCharts_V2(
          token,
          from,
          to,
          currentSpace?.contractCode || ""
        );
        setYearData({ ...charts });
      } catch {
        setHasError("Falha ao carregar os gráficos");
      } finally {
        setIsLoading(false);
      }
    },
    [token, currentSpace]
  );

  useEffect(() => {
    // filter get updated whenever search changes:
    // either on the first render of a tab, or when the user
    // selects a new date with the picker

    let dateData;
    const today = new Date();
    const urlParams = new URLSearchParams(search);

    if (!search) {
      dateData = parseDateFromToday(today);
    } else {
      dateData = parseDateFromUrl(today, urlParams);
    }

    const { referenceDate, month, year } = dateData;
    setReferenceDate(referenceDate);
    setBackendFilters({ month, year });
  }, [search]);

  useEffect(() => {
    // fetch the data again whenever the user changes
    // the selected date or switches tabs
    if (backendFilters) {
      setIsLoading(true);
      const { month, year } = backendFilters;

      if (isYearly) {
        fetchYearData({ month, year });
      } else {
        fetchMonthData({ month, year });
      }
    }
  }, [backendFilters, isYearly, fetchMonthData, fetchYearData]);

  const spaceHasBilling = useCallback((space: AutomationSpace): boolean => {
    return !!space.contractCode;
  }, []);

  const handleNoSpaceAvailable = useCallback((): void => {
    history.push("/onboarding/no-invoices-credentials");
  }, [history]);

  const downloadInvoicePDF = useCallback(
    async (downloadUrl) => {
      setIsDownloading(true);
      try {
        const blobURL = await fetchInvoicePDF(token, downloadUrl);
        return blobURL;
      } catch (error) {
        handleError(error, "Falha no download do PDF");
      } finally {
        setIsDownloading(false);
      }
    },
    [token]
  );

  const handleChange = (newDate: Date) => {
    const today = new Date();
    const { month, year } = parseDateFromPicker(newDate, today, isYearly);

    const urlParams = new URLSearchParams({ year, month }).toString();
    history.replace(`${pathname}?${urlParams}`);
  };

  const handleAlertClose = () => {
    setHasAlert(false);
  };

  const handleError = (error, altMessage) => {
    const message = error instanceof Error ? error.message : altMessage;
    setHasError(message);
  };

  const pageDetails = useMemo(() => {
    if (pathname.endsWith("/invoice-detail")) {
      return {
        title: "Análise da Fatura",
        subtitle: "Minhas Faturas",
        tabs: undefined,
      };
    } else {
      return {
        title: "Minha Economia",
        tabs: tabLinkItems,
        yearOnly: isYearly,
        displaySelector: true,
      };
    }
  }, [pathname, isYearly]);

  return (
    <PageStandard
      {...pageDetails}
      referenceDate={referenceDate}
      onDatechange={handleChange}
      customDisplayText={isYearly && !search ? "Últimos 12 meses" : ""}
      selectorFilter={spaceHasBilling}
      onNoSpaceAvailable={handleNoSpaceAvailable}
    >
      <BillingProvider
        store={{
          isLoading,
          monthData,
          yearData,
          hasError,
          downloadInvoicePDF,
          isDownloading,
          invoice: monthData?.invoiceApproval,
        }}
      >
        <Switch>
          <Redirect exact from={`${path}/`} to={`${path}/monthly`} />
          <Route exact path={`${path}/monthly`} component={MonthlyBilling} />
          <Route
            exact
            path={`${path}/invoice-detail`}
            component={InvoiceDetails}
          />
          <Route exact path={`${path}/yearly`} component={YearlyBilling} />
        </Switch>
      </BillingProvider>
      {hasAlert && monthData?.statusEnergyOperator && (
        <CredentialAlert
          // @ts-expect-error hasAlert is handling the "VALIDATED" case
          status={monthData.statusEnergyOperator}
          onClose={handleAlertClose}
        />
      )}
    </PageStandard>
  );
};
